import { createFileRoute, Link } from '@tanstack/react-router';
import { api } from '@repo/backend';
import { useQuery } from 'convex/react';
import { formatIDR } from '@/lib/format';
import { CountdownTimer, RarityBadge, BidForm, BidHistory, LiveViewerBadge } from '@/components/auction';
import { StatCard, FieldLabel, IronDivider } from '@/components/ui';
import { useAuctionDetail, useBidHistory } from '@/hooks/useAuctions';
import { usePlaceBid } from '@/hooks/useBid';
import type { BidEntry } from '@/data/auction';

export const Route = createFileRoute('/auction/$auctionId')({
  component: AuctionDetailPage,
});

// ─── Fallback image ───────────────────────────────────────────────────────────

const FALLBACK_IMG =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="300" height="400"><rect fill="%231C1D1E" width="300" height="400"/><text x="150" y="210" text-anchor="middle" fill="%233E4142" font-size="16" font-family="serif">No Image</text></svg>';

// ─── Page ─────────────────────────────────────────────────────────────────────

function AuctionDetailPage() {
  const { auctionId } = Route.useParams();

  const detail = useAuctionDetail(auctionId);
  const { results: bidResults } = useBidHistory(auctionId, 10);
  const currentUser = useQuery(api.users.queries.me);
  const seller = useQuery(
    api.users.queries.getById,
    detail?.auction?.sellerId ? { userId: detail.auction.sellerId } : 'skip',
  );
  const { placeBid, state: bidState } = usePlaceBid();
  const isSsr = typeof window === 'undefined';
  const redirectTarget = isSsr
    ? `/auction/${auctionId}`
    : window.location.href.replace(window.location.origin, '');

  // ── Loading ────────────────────────────────────────────────────────────────

  if (detail === undefined) {
    return <AuctionDetailSkeleton />;
  }

  // ── Not found ──────────────────────────────────────────────────────────────

  if (detail === null) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-6 px-8 text-center">
        <div className="w-px h-16 bg-primary/60" />
        <h1 className="text-4xl text-iron-100 tracking-widest font-epic">
          Item Not Found
        </h1>
        <p className="text-iron-400 font-body italic text-lg">
          This relic does not exist in our archives.
        </p>
        <Link
          to="/"
          className="mt-4 text-[10px] tracking-[0.3em] uppercase font-bold text-iron-300 border border-iron-700 px-6 py-3 hover:bg-iron-800 transition-colors"
        >
          ← Return to Auction Hall
        </Link>
      </div>
    );
  }

  const { auction, card, minNextBid } = detail;

  // ── Adapt Convex bid rows → BidEntry ──────────────────────────────────────

  const bidEntries: BidEntry[] = bidResults.map(({ bid, bidder }) => ({
    id: bid._id,
    username: bidder?.name ?? 'Anonymous Adventurer',
    amount: bid.amount,
    timestamp: bid.createdAt,
  }));

  const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  const rarityDisplay = card ? capitalize(card.rarity) : '—';
  const editionDisplay = card ? `${capitalize(card.rarity)} • ${capitalize(card.cardType)}` : '';

  const isAuctionOpen = auction.status === 'open' && Date.now() < auction.endsAt;
  const myUsername = currentUser?.name ?? currentUser?.email ?? 'Adventurer';

  let bidFormMode: 'loading' | 'authenticated' | 'guest' = 'guest';
  if (currentUser === undefined) bidFormMode = 'loading';
  else if (currentUser !== null) bidFormMode = 'authenticated';

  async function handleBid(amount: number) {
    await placeBid(auctionId, amount);
  }

  return (
    <div className="min-h-screen bg-background text-foreground">

      {/* ── Breadcrumb ── */}
      <div className="border-b border-iron-800 bg-iron-950/60">
        <div className="container mx-auto max-w-7xl px-8 py-3 flex items-center gap-3">
          <Link
            to="/"
            className="text-[10px] tracking-[0.25em] uppercase text-iron-500 hover:text-secondary transition-colors font-bold"
          >
            The Grand Auction
          </Link>
          <span className="text-iron-700">/</span>
          <span className="text-[10px] tracking-[0.25em] uppercase text-iron-300 font-bold truncate">
            {card?.name ?? auctionId}
          </span>
        </div>
      </div>

      <div className="container mx-auto max-w-7xl px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20">

          {/* ══ Left column: item detail ══ */}
          <div className="space-y-8">

            {/* Card Image */}
            <div className="bg-iron-950 border border-iron-800 flex items-center justify-center min-h-[480px] relative overflow-hidden group">
              <div className="absolute inset-0 bg-gradient-to-b from-secondary/3 to-transparent pointer-events-none" />
              <img
                src={card?.imageUrl ?? FALLBACK_IMG}
                alt={card?.name ?? 'Card'}
                className="max-h-[440px] w-auto object-contain p-8 transition-transform duration-700 group-hover:scale-105 drop-shadow-[0_16px_40px_rgba(0,0,0,0.8)]"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = FALLBACK_IMG;
                }}
              />
              <div className="absolute top-4 left-4">
                <RarityBadge rarity={rarityDisplay} />
              </div>
              <LiveViewerBadge auctionId={auctionId} variant="overlay" className="absolute top-4 right-4" />
              <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-secondary/40 to-transparent" />
            </div>

            {/* Item Info */}
            <div className="bg-iron-900 border border-iron-800 p-8">
              <FieldLabel className="mb-3">{editionDisplay}</FieldLabel>
              <h1 className="text-4xl text-iron-100 tracking-[0.2em] leading-tight mb-6">
                {card?.name ?? '—'}
              </h1>
              <IronDivider className="mb-6" />
              <p className="font-body text-iron-400 italic text-base leading-relaxed mb-8">
                {card?.description ?? card?.flavorText ?? 'No description available.'}
              </p>
              <IronDivider className="mb-6" />
              <div>
                <FieldLabel className="mb-1">Owner</FieldLabel>
                <p className="text-iron-200 font-bold tracking-wide">
                  {seller?.name ?? seller?.email ?? 'Anonymous Collector'}
                </p>
              </div>
            </div>

            {/* Key stats */}
            <div className="grid grid-cols-3 gap-4">
              <StatCard label="Start Bid" value={formatIDR(auction.startingPrice)} />
              <StatCard label="Total Bids" value={String(auction.bidCount)} />
              <StatCard label="Rarity" value={rarityDisplay} accent />
            </div>
          </div>

          {/* ══ Right column: bidding ══ */}
          <div className="space-y-8">

            {/* Countdown */}
            <div className="bg-iron-900 border border-iron-800 p-8">
              <FieldLabel className="mb-6">Time Remaining</FieldLabel>
              <CountdownTimer endsAt={auction.endsAt} variant="block" />
            </div>

            {/* Bid form or closed state */}
            {isAuctionOpen ? (
              <BidForm
                currentHighest={auction.currentBid}
                minNextBid={minNextBid}
                myUsername={myUsername}
                onBid={handleBid}
                mode={bidFormMode}
                submitState={bidState.status}
                errorMessage={bidState.status === 'error' ? bidState.message : undefined}
                redirectTo={redirectTarget}
              />
            ) : (
              <AuctionClosedPanel status={auction.status} />
            )}

            {/* Bid history — live from Convex */}
            <BidHistory entries={bidEntries} myUsername={currentUser?.name ?? ''} />

            {/* Live viewer bar */}
            <LiveViewerBadge auctionId={auctionId} variant="bar" />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── AuctionClosedPanel ───────────────────────────────────────────────────────

function AuctionClosedPanel({ status }: { status: string }) {
  const labelByStatus: Record<string, string> = {
    closed: 'This auction has ended.',
    cancelled: 'This auction was cancelled.',
    pending: 'This auction has not started yet.',
  };
  const label = labelByStatus[status] ?? 'This auction has ended.';

  return (
    <div className="bg-iron-900 border border-iron-800 p-8 flex flex-col items-center gap-4 text-center">
      <div className="w-px h-8 bg-iron-700" />
      <p className="text-iron-400 font-body italic">{label}</p>
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function AuctionDetailSkeleton() {
  return (
    <div className="min-h-screen bg-background text-foreground animate-pulse">
      <div className="border-b border-iron-800 bg-iron-950/60 h-10" />
      <div className="container mx-auto max-w-7xl px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 xl:gap-20">
          <div className="space-y-8">
            <div className="h-[480px] bg-iron-900 border border-iron-800" />
            <div className="h-48 bg-iron-900 border border-iron-800" />
            <div className="grid grid-cols-3 gap-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-16 bg-iron-900 border border-iron-800" />
              ))}
            </div>
          </div>
          <div className="space-y-8">
            <div className="h-32 bg-iron-900 border border-iron-800" />
            <div className="h-64 bg-iron-900 border border-iron-800" />
            <div className="h-48 bg-iron-900 border border-iron-800" />
          </div>
        </div>
      </div>
    </div>
  );
}
